import { gql } from 'urql';
import { BranchInfo } from '../fragments/branch.graphql';
import { CharacterDetails } from '../fragments/character.graphql';
import { LocationDetails } from '../fragments/location.graphql';
import { ModSignup } from '../fragments/modSignup.graphql';

export const getEvents = gql`
  query GetEvents(
    $from: String
    $upto: String
    $organizationIds: [Int!]
    $branchIds: [Int!]
  ) {
    events(
      from: $from
      upto: $upto
      organizationIds: $organizationIds
      branchIds: $branchIds
    ) {
      id
      name
      kind
      description
      startsAt
      endsAt
      registrationOpensAt
      kind
      isVirtual
      location {
        ...LocationDetails
      }
      branch {
        ...BranchInfo
      }
      config {
        baseBuild
        maxExtraXp
        considersTravelersLocal
      }
    }
  }
  ${BranchInfo}
  ${LocationDetails}
`;

export const getEventModSections = gql`
  query GetEventModSections($eventId: Int!) {
    event(eventId: $eventId) {
      id
      modSections {
        id
        name
        signupLimit
        mods {
          id
          name
          timeframe
          participationLimit
          description
          constraint
          modSignups {
            ...ModSignup
          }
          modRunner {
            id
            user {
              id
              fullName
            }
          }
        }
      }
    }
  }
  ${ModSignup}
`;

export const getEventModParticipants = gql`
  query GetEventModParticipants($eventId: Int!, $modId: Int!) {
    event(eventId: $eventId) {
      id
      mod(id: $modId) {
        id
        modSignups {
          id
          createdAt
          eventAttendee {
            id
            user {
              id
              fullName
            }
            attendeeCharacters {
              id
              character {
                id
              }
              actionLogs {
                id
                tally {
                  armor
                  body
                  mind
                  resolve
                  skillUses {
                    id
                    characterSkillId
                    remainingUses
                    level
                  }
                }
              }
            }
          }
          character {
            ...CharacterDetails
          }
        }
      }
    }
  }
  ${CharacterDetails}
`;

export const getEventInfo = gql`
  query GetEvent($eventId: Int!) {
    event(eventId: $eventId) {
      id
      name
      description
      kind
      startsAt
      endsAt
      registrationOpensAt
      kind
      isEventManager
      isEventAssistant
      isEventRegistrar
      modSignupOpensAt
      modMaxSignup
      modEnableWaitlist
      nextEvent {
        id
      }
      prevEvent {
        id
      }
      config {
        baseBuild
        maxCurveXp
        extraXpCost
        maxExtraXp
        maxExtraXpLimit
        shiftUnit
        shiftMultiplier
        maxShift
        shiftKinds
        considersTravelersLocal
      }
      branch {
        ...BranchInfo
      }
      location {
        ...LocationDetails
      }
    }
  }
  ${BranchInfo}
  ${LocationDetails}
`;

export const getEventTemporaryManagers = gql`
  query GetEventTemporaryManagers($eventId: Int!) {
    event(eventId: $eventId) {
      id
      temporaryManagers {
        id
        user {
          id
          fullName
          userOrganization {
            id
            role
            assistantFlags
            branch {
              id
              shorthand
            }
          }
          ownedBranches {
            id
            shorthand
          }
        }
      }
    }
  }
`;

export const getEventTickets = gql`
  query GetEventTickets($eventId: Int!, $playerId: Int, $editMode: Boolean) {
    event(eventId: $eventId) {
      id
      tickets(playerId: $playerId, editMode: $editMode) {
        id
        cost
        extraXpCost
        label
        limit
        discountType
        passType
        restrictionType
        shiftResponsibilities
        econRestriction
        fixedLimit
        fullyBooked
        eventAttendeesCount
        modMaxSignup
      }
    }
  }
`;

export const getEventShifts = gql`
  query GetEventShifts($eventId: Int!) {
    event(eventId: $eventId) {
      id
      shiftKinds
      shifts(withFulfillments: true) {
        id
        label
        baseRatio
        limit
        kind
        identifier
        isEnabled
        autoExpand
        adjustableExpansionStrategy
        playersCount
        attendeeShifts {
          id
          status
          player {
            id
            fullName
            roleInOrganization
          }
        }
      }
    }
  }
`;

export const getEventAttendances = gql`
  query GetEventAttendances($eventId: Int!, $ticketId: Int) {
    event(eventId: $eventId) {
      id
      eventAttendees(ticketId: $ticketId) {
        id
        buildGrowth
        kind
        paid
        attending
        costOverride
        referralInfo
        ticketId
        ticket {
          id
          label
          restrictionType
          discountType
          cost
          extraXpCost
        }
        user {
          id
          fullName
        }
        attendeeCharacters {
          id
          character {
            id
            name
          }
        }
      }
    }
  }
`;

export const getEventPrintouts = gql`
  query GetEventPrintouts(
    $eventId: Int!
    $paid: Boolean
    $printRequested: Boolean
    $homeGame: Boolean
    $ticketIds: [Int!]
  ) {
    event(eventId: $eventId) {
      id
      name
      startsAt
      endsAt
      branch {
        id
        shorthand
      }
      location {
        id
        name
      }
      printouts(
        paid: $paid
        printRequested: $printRequested
        homeGame: $homeGame
        ticketIds: $ticketIds
      ) {
        id
        user {
          id
          fullName
          membershipActiveDuringEvent(eventId: $eventId)
          totalCapsForEvent(eventId: $eventId)
          roleInOrganization
          userOrganization {
            id
            role
            branch {
              id
              shorthand
            }
          }
        }
        shifts {
          id
          kind
          label
        }
        ticket {
          id
          label
        }
        attendeeCharacters {
          id
          printRequestStatus
          character {
            ...CharacterDetails
          }
        }
      }
    }
  }
  ${CharacterDetails}
`;

export const getEventTransactions = gql`
  query GetEventTransactions($eventId: Int!, $modId: Int, $characterId: Int) {
    event(eventId: $eventId) {
      id
      itemTransactions(modId: $modId, characterId: $characterId) {
        id
        item {
          id
          kind
          name
        }
        status
        priority
        stack
        expiresAt
        skillId
        durationInMinute
        mindCost
        resolveCost
        armorUpgradePoints
        mailingAddress
        itemCrafting {
          id
          blueprint {
            id
            name
          }
        }
        registeredItem {
          id
          description
        }
        character {
          id
          name
          user {
            id
            fullName
          }
        }
        transactionPartner {
          id
          name
          user {
            id
            fullName
          }
        }
        postie {
          id
          fullName
        }
        mailoutRecipient {
          id
          fullName
        }
        source
        mod {
          id
          name
        }
        description
        createdAt
        childTransactions {
          id
          item {
            id
            kind
            name
          }
          stack
          expiresAt
          source
          registeredItem {
            id
            description
          }
          character {
            id
            name
            user {
              id
              fullName
            }
          }
          transactionPartner {
            id
            name
            user {
              id
              fullName
            }
          }
        }
      }
    }
  }
`;

export const getEventItemTransactionStatistics = gql`
  query GetEventItemTransactionStatistics($eventId: Int!) {
    event(eventId: $eventId) {
      id
      itemTransactionStatistics {
        itemId
        itemName
        itemKind
        source
        distribution
        stack
      }
    }
  }
`;
